import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../../utils/api";

// Pivot: rows = product (Item ID + name), cols = Front / Back / Other, values = scans in the selected window
const ScansByProductGrid = ({ queryParams, startDate, endDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ col: "total", dir: "desc" });

  useEffect(() => {
    setLoading(true);
    api
      .get(`/api/analytics/scans-by-product?${queryParams}`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [queryParams]);

  const toggleSort = (col) => {
    setSort((prev) => ({ col, dir: prev.col === col && prev.dir === "desc" ? "asc" : "desc" }));
  };

  const sortedRows = () => {
    if (!data) return [];
    const rows = [...data.rows];
    const { col, dir } = sort;
    rows.sort((a, b) => {
      const av = a[col], bv = b[col];
      const cmp = typeof av === "number" ? av - bv : String(av || "").localeCompare(String(bv || ""));
      return dir === "asc" ? cmp : -cmp;
    });
    return rows;
  };

  const exportToExcel = () => {
    if (!data) return;
    const rows = sortedRows().map((r) => ({
      "Item ID": r.itemId,
      "Product": r.productName + (r.productDeleted ? " (deleted)" : ""),
      "Retailer": r.retailer,
      "UPC": r.upc,
      "Front": r.front,
      "Back": r.back,
      "Other": r.other,
      "Total": r.total,
    }));
    rows.push({ "Item ID": "", "Product": "TOTAL", "Retailer": "", "UPC": "", "Front": data.totals.front, "Back": data.totals.back, "Other": data.totals.other, "Total": data.totals.total });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scans_by_Product");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const range = `${startDate || "all"}_to_${endDate || "today"}`;
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Scans_by_Product_${range}.xlsx`);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" role="status" />
      </div>
    );
  }

  if (!data || data.rows.length === 0) {
    return <p className="text-muted text-center mb-0">No scans in the selected date range.</p>;
  }

  const rows = sortedRows();
  const showOther = data.totals.other > 0;
  const th = (label, col, right) => (
    <th
      onClick={() => toggleSort(col)}
      style={{ cursor: "pointer", textAlign: right ? "right" : "left", whiteSpace: "nowrap", userSelect: "none" }}
      title="Click to sort"
    >
      {label}{sort.col === col ? (sort.dir === "desc" ? " ▼" : " ▲") : ""}
    </th>
  );
  const num = (n) => n.toLocaleString();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          {rows.length} product{rows.length === 1 ? "" : "s"} · {num(data.totals.total)} scans
          {data.unresolvedScans > 0 && <span> · {num(data.unresolvedScans)} scans on unknown codes excluded</span>}
        </small>
        <button className="btn btn-sm btn-outline-secondary" onClick={exportToExcel}>Export to Excel</button>
      </div>
      <div style={{ maxHeight: "480px", overflow: "auto" }}>
        <table className="table table-sm table-hover" style={{ fontSize: "13px" }}>
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>
              {th("Item ID", "itemId")}
              {th("Product", "productName")}
              {th("Retailer", "retailer")}
              {th("Front", "front", true)}
              {th("Back", "back", true)}
              {showOther && th("Other", "other", true)}
              {th("Total", "total", true)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.productId || "unassigned"} className={r.productDeleted ? "text-muted" : ""}>
                <td style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{r.itemId || "—"}</td>
                <td>
                  {r.productName}
                  {r.productDeleted && <span className="badge bg-secondary ms-1" style={{ fontSize: "9px" }}>deleted</span>}
                  {(r.codes.front > 1 || r.codes.back > 1) && (
                    <span className="text-muted ms-1" style={{ fontSize: "11px" }} title="More than one code on a side; scans are summed">
                      ({r.codes.front + r.codes.back + r.codes.other} codes)
                    </span>
                  )}
                </td>
                <td>{r.retailer || "—"}</td>
                <td style={{ textAlign: "right" }}>{num(r.front)}</td>
                <td style={{ textAlign: "right" }}>{num(r.back)}</td>
                {showOther && <td style={{ textAlign: "right" }}>{num(r.other)}</td>}
                <td style={{ textAlign: "right", fontWeight: 600 }}>{num(r.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot style={{ position: "sticky", bottom: 0, background: "#f8f9fa", fontWeight: 600 }}>
            <tr>
              <td colSpan={3}>Total</td>
              <td style={{ textAlign: "right" }}>{num(data.totals.front)}</td>
              <td style={{ textAlign: "right" }}>{num(data.totals.back)}</td>
              {showOther && <td style={{ textAlign: "right" }}>{num(data.totals.other)}</td>}
              <td style={{ textAlign: "right" }}>{num(data.totals.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ScansByProductGrid;
